/**
 * UploadStatusTracker — polls GET /api/datapool/batches/{id}/status and
 * renders the staged pipeline's progress. Every stage is one chip so
 * the user sees exactly what the backend is doing:
 *
 *   Uploaded ─▶ Intaking ─▶ Parsing ─▶ Normalising ─▶ Classifying
 *   ─▶ Allocating ─▶ Applying ─▶ Reporting ─▶ Applied / Review / Failed
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  CheckCircle2, AlertTriangle, XCircle, Loader2, FileText,
  Sparkles, LayoutDashboard,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ordered list of stages the UI renders as chips. Every batch walks
// through these in order; once the backend reports one as reached, the
// chip is lit.
const STAGES = [
  { key: "uploaded",       label: "Uploaded" },
  { key: "intaking",       label: "Preparing" },
  { key: "parsing",        label: "Parsing documents" },
  { key: "normalising",    label: "Normalising records" },
  { key: "classifying",    label: "Classifying transactions" },
  { key: "allocating",     label: "Allocating to vaults" },
  { key: "applying",       label: "Applying to ledger" },
  { key: "reporting",      label: "Generating report" },
  { key: "applied",        label: "Completed" },
];

const STAGE_INDEX = Object.fromEntries(STAGES.map((s, i) => [s.key, i]));
const TERMINAL = new Set(["applied", "report_ready", "review_required", "failed"]);

const formatMoney = (n, ccy = "GBP") => {
  const v = Number(n) || 0;
  const sign = v < 0 ? "-" : v > 0 ? "+" : "";
  const sym = ccy === "GBP" ? "£" : ccy === "EUR" ? "€" : ccy === "USD" ? "$" : "";
  return `${sign}${sym}${Math.abs(v).toLocaleString("en-GB", {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  })}`;
};

const VAULT_LABEL = {
  FREE_EQUITY: "Free Equity", TAX_CUSTODY: "Tax Custody",
  SUPPLIER_OBLIGATION: "Supplier Obligation",
  CUSTOMER_TRUST: "Customer Trust",
  INVENTORY_CUSTODY: "Inventory Custody",
  OPERATING_CUSTODY: "Operating Custody",
  CREDIT_FACILITY_CUSTODY: "Credit Facility",
  LOAN_OBLIGATION: "Loan Obligation",
  RESERVE_CAPITAL: "Reserve Capital",
  STRATEGIC_CAPITAL: "Strategic Capital",
};

export default function UploadStatusTracker({ batchId, onComplete }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  const poll = useCallback(async () => {
    try {
      const token = localStorage.getItem("sv_token");
      const res = await axios.get(
        `${API}/datapool/batches/${batchId}/status`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 },
      );
      setStatus(res.data);
      return res.data;
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e.message);
      return null;
    }
  }, [batchId]);

  useEffect(() => {
    if (!batchId) return;
    let active = true;
    let timer;
    const tick = async () => {
      const data = await poll();
      if (!active) return;
      if (data && TERMINAL.has(data.pipeline_status)) {
        onComplete?.(data);
        return;
      }
      timer = setTimeout(tick, 1500);
    };
    tick();
    return () => { active = false; if (timer) clearTimeout(timer); };
  }, [batchId, poll, onComplete]);

  if (!batchId) return null;

  const ps = status?.pipeline_status || "uploaded";
  const activeIdx = STAGE_INDEX[ps] ?? -1;
  const terminal = TERMINAL.has(ps);
  const isFailed = ps === "failed";
  const isReview = ps === "review_required";
  const isApplied = ps === "applied" || ps === "report_ready";

  return (
    <div
      className="rounded-xl border border-slate-800/70 bg-slate-900/40 p-5"
      data-testid="upload-status-tracker"
    >
      <Header status={status} ps={ps} />

      {/* Stage chips */}
      <ol
        className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-2"
        data-testid="upload-status-stages"
      >
        {STAGES.map((s, i) => {
          let state = "pending";
          if (isFailed && i > activeIdx) state = "skipped";
          else if (i < activeIdx || (terminal && isApplied && s.key === "applied")) state = "done";
          else if (i === activeIdx && !terminal) state = "active";
          else if (terminal && i === activeIdx) state = "done";
          return (
            <li
              key={s.key}
              data-testid={`stage-chip-${s.key}`}
              data-state={state}
              className={[
                "flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] transition-colors",
                state === "done"    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200" :
                state === "active"  ? "border-cyan-500/50 bg-cyan-500/15 text-cyan-100 animate-pulse" :
                state === "skipped" ? "border-slate-800 bg-slate-900/30 text-slate-600 line-through" :
                                      "border-slate-800 bg-slate-900/30 text-slate-500",
              ].join(" ")}
            >
              {state === "active"
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : state === "done"
                  ? <CheckCircle2 className="w-3 h-3" />
                  : <span className="w-3 h-3 rounded-full border border-current opacity-60" />}
              {s.label}
            </li>
          );
        })}
      </ol>

      {/* Terminal state card */}
      {terminal && (
        <TerminalCard
          status={status} error={error}
          isApplied={isApplied} isReview={isReview} isFailed={isFailed}
        />
      )}

      {error && !terminal && (
        <div
          className="mt-3 text-[11px] text-amber-400"
          data-testid="upload-status-poll-error"
        >
          Polling error: {error}
        </div>
      )}
    </div>
  );
}


function Header({ status, ps }) {
  const msg = status?.pipeline_message || "Preparing pipeline…";
  return (
    <div className="flex items-center gap-3">
      <FileText className="w-4 h-4 text-cyan-400" />
      <div className="flex-1 min-w-0">
        <div
          className="text-[11px] uppercase tracking-wider text-slate-400"
          data-testid="upload-status-stage-name"
        >
          {ps}
        </div>
        <div
          className="text-sm text-slate-200 truncate"
          data-testid="upload-status-message"
        >
          {msg}
        </div>
      </div>
    </div>
  );
}


function TerminalCard({ status, error, isApplied, isReview, isFailed }) {
  if (isFailed) {
    return (
      <div
        className="mt-4 rounded-lg border border-red-500/40 bg-red-500/10 p-3"
        data-testid="upload-status-terminal-failed"
      >
        <div className="flex items-center gap-2 text-red-300 text-sm font-semibold">
          <XCircle className="w-4 h-4" /> Processing failed
        </div>
        <p className="mt-1 text-[12px] text-red-200/80">
          No financial truth was applied. Your dashboard was not changed.
        </p>
        {status?.last_error && (
          <pre className="mt-2 text-[10px] text-red-200/60 whitespace-pre-wrap break-all">
            {status.last_error}
          </pre>
        )}
      </div>
    );
  }

  if (isReview) {
    const needs = status?.records_needs_review || 0;
    const applied = status?.records_auto_applied || 0;
    return (
      <div
        className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3"
        data-testid="upload-status-terminal-review"
      >
        <div className="flex items-center gap-2 text-amber-300 text-sm font-semibold">
          <AlertTriangle className="w-4 h-4" /> Needs your review
        </div>
        <p className="mt-1 text-[12px] text-amber-200/90">
          {applied} transaction{applied === 1 ? "" : "s"} applied.{" "}
          {needs} need{needs === 1 ? "s" : ""} your review before they can land.
        </p>
      </div>
    );
  }

  if (isApplied) {
    const applied = status?.records_auto_applied || 0;
    const needsReview = status?.records_needs_review || 0;
    const duplicates = status?.records_duplicate || status?.ledger_entries_skipped_duplicate || 0;
    const aiCalls = status?.ai_calls || 0;
    const memoryHits = status?.memory_hits || 0;
    const summary = status?.summary;
    const deltas = status?.vault_deltas || {};
    const deltaRows = Object.entries(deltas);
    const ccys = status?.currencies_detected || [];
    const ccySummary = status?.currency_summary || {};
    return (
      <div
        className="mt-4 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3"
        data-testid="upload-status-terminal-applied"
      >
        <div
          className="flex items-center gap-2 text-emerald-300 text-sm font-semibold"
          data-testid="upload-status-applied-headline"
        >
          <CheckCircle2 className="w-4 h-4" />
          {applied} transaction{applied === 1 ? "" : "s"} allocated to vaults. Dashboard updated.
        </div>
        {summary && (
          <div
            className="mt-1 text-[12px] text-emerald-100/80 italic"
            data-testid="upload-status-narrative"
          >
            {summary}
          </div>
        )}
        <div
          className="mt-2 flex flex-wrap gap-2 text-[11px]"
          data-testid="upload-status-counters"
        >
          <span className="rounded bg-emerald-500/15 border border-emerald-500/40 px-1.5 py-0.5 text-emerald-200" data-testid="counter-applied">
            {applied} applied
          </span>
          {needsReview > 0 && (
            <span className="rounded bg-amber-500/15 border border-amber-500/40 px-1.5 py-0.5 text-amber-200" data-testid="counter-review">
              {needsReview} need{needsReview === 1 ? "s" : ""} review
            </span>
          )}
          {duplicates > 0 && (
            <span className="rounded bg-slate-500/15 border border-slate-500/40 px-1.5 py-0.5 text-slate-200" data-testid="counter-duplicate">
              {duplicates} duplicate{duplicates === 1 ? "" : "s"} skipped
            </span>
          )}
          {memoryHits > 0 && (
            <span className="rounded bg-cyan-500/15 border border-cyan-500/40 px-1.5 py-0.5 text-cyan-200" data-testid="counter-memory">
              {memoryHits} from memory
            </span>
          )}
          {aiCalls > 0 && (
            <span className="rounded bg-fuchsia-500/15 border border-fuchsia-500/40 px-1.5 py-0.5 text-fuchsia-200" data-testid="counter-ai">
              {aiCalls} AI fallback
            </span>
          )}
        </div>
        {ccys.length > 0 && (
          <div
            className="mt-2 text-[12px] text-emerald-200/80"
            data-testid="upload-status-currencies-detected"
          >
            Currencies detected:{" "}
            {ccys.map((c) => (
              <span
                key={c}
                className="inline-block rounded bg-slate-900/40 border border-slate-700 px-1.5 py-0.5 mx-0.5 text-emerald-200 font-mono"
                data-testid={`currency-chip-${c}`}
              >
                {c}{ccySummary[c] !== undefined ? ` ${formatMoney(ccySummary[c], c)}` : ""}
              </span>
            ))}
          </div>
        )}
        {deltaRows.length > 0 && (
          <div
            className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2"
            data-testid="upload-status-vault-deltas"
          >
            {deltaRows.map(([vault, byCcy]) => (
              <div key={vault} className="rounded-md bg-slate-900/50 border border-slate-800/60 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-400">
                  {VAULT_LABEL[vault] || vault}
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                  {Object.entries(byCcy).map(([ccy, total]) => (
                    <span
                      key={ccy}
                      className={`text-sm font-mono tabular ${total >= 0 ? "text-emerald-300" : "text-red-300"}`}
                      data-testid={`vault-delta-${vault}-${ccy}`}
                    >
                      {formatMoney(total, ccy)}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-400">
          <Sparkles className="w-3 h-3 text-cyan-400" />
          <LayoutDashboard className="w-3 h-3" />
          Dashboard reflects the new balances.
        </div>
      </div>
    );
  }

  return null;
}
